import { ConnectedAccountStatus, ElectionStanding, FeatureType, GetBallotResponse, GetElectionResponse, GetOrganizationProductResponse, GetPointResponse, ObjectId, OrganizationProductStatus, OrganizationPurchaseHistory, PaymentTargetType, PurchaseStatus, TopicCategory, VoterDetail } from '../../../types';
import { LitElement, css, html } from 'lit';
import { convertLocaleToUTC, convertUTCToLocale } from '../../../helpers/date';
import { customElement, query, state } from 'lit/decorators';
import { delay, isDefined } from '../../../helpers/utils';
import { getBallotsForElection, getElectionLeaders } from '../../../helpers/api/election-api';

import { CreateProductComponent } from '../../create-product-component';
import { DatePicker } from '@vaadin/vaadin-date-picker';
import { Dialog } from '@material/mwc-dialog';
import { ElectionFormComponent } from '../../election-form-component';
import { ErrorToastElement } from '../../error-toast';
import { FormSwitch } from '../../theme/form-switch';
import { RichTextEditor } from '../../common/rich-text-editor';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import accountInfo from '../../../helpers/state/account-info';
import { dummyInitialElection } from '../../../helpers/constants';
import { escapeCsvValue } from '../../../helpers/files';
import featuresInfo from '../../../helpers/state/features-info';
import format from 'date-fns/format';
import { getMainPage } from '../../../helpers/page-name';
import { getOrganizationProducts } from '../../../helpers/api/products-api';
import { getOrganizationPurchaseHistory } from '../../../helpers/api/purchases-api';
import { loadStripeStatus } from '../../../helpers/api/stripe-api';
import { openChatWindow } from '../../../helpers/intercom';
import organizationInfo from '../../../helpers/state/organization-info';
import pointCache from '../../../helpers/state/point-cache';
import { timezoneAsync } from '../../../helpers/api/google-api';
import topicData from '../../../helpers/state/topic-data';

@customElement('admin-election-addon')
export class AdminElectionAddon extends LitElement {
    amplitude_callsite: string = 'Election Settings Drawer';

    @query('#errorToast')
    private readonly errorToastElement?: ErrorToastElement;

    @query('#electionModal')
    electionModal!: Dialog;

    @query('#electionFormPreview')
    electionFormPreviewElement!: ElectionFormComponent;

    @query('#startDate')
    public startDateElement!: DatePicker;

    @query('#endDate')
    public endDateElement!: DatePicker;

    @query('#active')
    public activeElement!: FormSwitch;

    @query('#description')
    private readonly descriptionElement?: RichTextEditor | null;

    @query('#categoryDescriptionEditor')
    private readonly categoryDescriptionElement?: RichTextEditor | null;

    @query('#createProductComponent')
    createProductComponent?: CreateProductComponent;

    @state()
    selected_tab: string = 'Setup';

    @state()
    election_standings: ElectionStanding[] = [];

    @state()
    editingCategoryDescription?: string;

    @state()
    election?: GetElectionResponse;

    @state()
    ballots: GetBallotResponse[] = [];

    @state()
    uniqueParticipants: VoterDetail[] = [];

    @state()
    total_participants_count: number = 0;

    @state()
    total_votes_counts: number = 0;

    @state()
    total_ballots_count: number = 0;

    @state()
    electionStartDateLocal: string = '';

    @state()
    electionEndDateLocal: string = '';

    @state()
    sortedNominees: GetPointResponse[] = [];

    @state()
    timezoneID: string = '';

    @state()
    standings_category_filter?: string;

    @state()
    sortBy: string = '';

    @state()
    votersSortBy: string = '';

    @state()
    sortOrder: 'asc' | 'desc' = 'asc';

    @state()
    votersSortOrder: 'asc' | 'desc' = 'asc';

    @state()
    locationsOnLoad: number = 0;

    @state()
    ballotProducts: GetOrganizationProductResponse[] = [];

    @state()
    purchaseHistory: OrganizationPurchaseHistory | undefined;

    @state()
    stripeInfo: ConnectedAccountStatus | undefined;

    @state()
    isEditingProduct: boolean = false;

    options: string[] = ['Setup', 'Results', 'Voters', 'Nominations', 'Payment', 'Services'];

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        // Wait for topic data to be ready.
        while (!topicData.isReady || !pointCache.isReady) {
            await delay(500);
        }

       // Wait for challenge data to be ready.
        const elections = topicData.allElections;
        this.election = elections[0] ?? dummyInitialElection;

        // get timezone of map center for date handling
        const timezoneInfos = await timezoneAsync(String(topicData.topic?.default_map_center.latLng.lat), String(topicData.topic?.default_map_center.latLng.lng));
        this.timezoneID = timezoneInfos[0]?.identifier;

        // convert stored start and end to local time because it is stored in UTC
        const startLocal = convertUTCToLocale(this.election.start_time as string, this.timezoneID);
        const endLocal = convertUTCToLocale(this.election.end_time as string, this.timezoneID);

        // populate elements with local time
        this.startDateElement.value = isDefined(this.election.start_time)? format(startLocal, 'yyyy-MM-dd') : '';
        this.endDateElement.value =  isDefined(this.election.end_time) ? format(endLocal, 'yyyy-MM-dd') : '';

        // populate local variables with local time in case user doesn't change anything
        this.electionStartDateLocal = this.startDateElement.value;
        this.electionEndDateLocal = this.endDateElement.value;

        // this.election_description = this.election.description ?? '';
        this.descriptionElement?.setInnerText(this.election.description ?? '');

        const excludedCategoryIds = this.election.excluded_categories.map((c) => c.$oid);

        const includedCategories = topicData.categories
            .filter((c) => c._id && !excludedCategoryIds.includes(c._id.$oid))
            .map((c) => c._id as ObjectId);

        const categoryDescriptions = includedCategories.reduce<Record<string, string>>((acc: Record<string, string>, c) => {
            const key = c.$oid;
            if (key) {
                acc[key] = this.election?.category_descriptions.find((cd) => cd.category_id === key)?.description ?? '';
            }
            return acc;
        }, {});

        this.editingCategoryDescription = includedCategories[0]?.$oid;
        this.categoryDescriptionElement?.setInnerText(categoryDescriptions[this.editingCategoryDescription] ?? '');

        // get all points except excluded points
        this.locationsOnLoad = pointCache.allPoints.filter((point) => this.election?.excluded_points?.includes(point._id) !== true).length;
        try {
            await this.getBallots();
        } catch (error) {
            getMainPage().errorToastElement?.showToastAndThrow('Error fetching ballots. Reach out to Proxi');
        }
        if (this.ballots.length > 0 && topicData.topic) {
            this.election_standings = await getElectionLeaders(this.election?._id?.$oid ?? '', topicData.topicId, topicData.topicKey);
        }
        this.uniqueParticipants = this.getUniqueParticipants();
        this.sortedNominees = pointCache.allPoints.filter((point) => this.election?.excluded_points?.includes(point._id) !== true || !this.election?.excluded_categories?.map((c) => c.$oid).includes(point._id.$oid));

        await this.getBallotProducts();
    }

    async getBallots() {
        if (isDefined(this.election?._id)) {
            this.ballots = await getBallotsForElection(this.amplitude_callsite, topicData.topicId, this.election?._id?.$oid ?? '', topicData.topicKey);
        }
    }

    async getBallotProducts() {
        this.isEditingProduct = false;
        try {
            this.ballotProducts = await getOrganizationProducts(PaymentTargetType.Ballot, this.election?._id.$oid ?? '', topicData.topic?.organization_id?.$oid ?? '', topicData.topicKey);
        } catch (error) {
            console.error('Failed to load ballot products:', error);
            getMainPage().errorToastElement.showToast('Failed to load ballot products.');
            this.ballotProducts = [];
        }
    }

    handleChangeTab(e: CustomEvent) {
        this.tabIndex = e.detail.tabIndex;
        this.selected_tab = this.options[this.tabIndex];

        if (this.selected_tab === 'Payment' && organizationInfo.organization?.is_administrator === true) {
            void this.loadPaymentData();
        }
    }

    async onChangeActiveStatus(e: CustomEvent) {
        const active = (e.target as FormSwitch).checked;
        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { active }, this.amplitude_callsite);
    }

    async onChangeNominationsStatus(e: CustomEvent) {
        const nomination_is_live = (e.target as FormSwitch).checked;
        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { nomination_is_live }, this.amplitude_callsite);
    }

    async onChangeStartDate(e: CustomEvent) {
        const newStartDate = this.startDateElement.value;
        const newstartDateUTC = convertLocaleToUTC(newStartDate, this.timezoneID)?.toISOString(); // send this with change

        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { start_time: newstartDateUTC }, this.amplitude_callsite);
    }

    async onChangeEndDate(e: CustomEvent) {
        const newEndDate = `${this.endDateElement.value}T23:59:00.000Z`;
        const newendDateUTC = convertLocaleToUTC(newEndDate, this.timezoneID)?.toISOString(); // send this with change
        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { end_time: newendDateUTC }, this.amplitude_callsite);
    }

    async onChangeElectionName(e: CustomEvent) {
        if (!this.election) {
            return;
        }
        this.election.name = (e.target as HTMLInputElement).value;
        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { name: this.election.name }, this.amplitude_callsite);
    }

    async onChangeElectionDescription(e: CustomEvent) {
        const election_description = (e.target as RichTextEditor).getInnerText();
        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { description: election_description }, this.amplitude_callsite);
    }

    async onChangeElectionEmailTerms(e: CustomEvent) {
        const election_email_terms = (e.target as HTMLInputElement).value;
        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { email_terms: election_email_terms }, this.amplitude_callsite);
    }

    async onChangeShowLeaders(e: CustomEvent) {
        const value = (e.target as HTMLInputElement).value;
        if (value === 'None') {
            this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { show_leaders: 0 }, this.amplitude_callsite);
        } else {
            this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { show_leaders: Number(value) }, this.amplitude_callsite);
        }
    }

    async toggleShowLeaders() {
        const current = this.election?.show_leaders !== 0 && this.election?.show_leaders !== undefined;
        if (current) {
            this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { show_leaders: 0 }, this.amplitude_callsite);
        } else {
            this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { show_leaders: 3 }, this.amplitude_callsite);
        }
    }

    async onChangePointButtonText(e: CustomEvent) {
        const point_button_text = (e.target as HTMLInputElement).value;
        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { point_button_text }, this.amplitude_callsite);
    }

    async onChangeSubmitButtonText(e: CustomEvent) {
        const submit_button_text = (e.target as HTMLInputElement).value;
        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { submit_button_text }, this.amplitude_callsite);
    }

    async onChangeMultiSelect(e: CustomEvent) {
        const multi_select = (e.target as FormSwitch).checked === false;
        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { multi_select }, this.amplitude_callsite);
    }

    async onChangeFrequencyRecurrence(e: CustomEvent) {
        const frequency_recurrance = (e.target as HTMLInputElement).value as 'daily' | 'weekly' | 'monthly' | 'once' | 'unlimited';
        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { frequency_recurrance }, this.amplitude_callsite);
    }

    async onChangeVerifyEmail(e: CustomEvent) {
        const verify_email = (e.target as FormSwitch).checked;
        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { verify_email }, this.amplitude_callsite);
    }

    async onChangeRequireOptIn(e: CustomEvent) {
        const email_opt_in_required = (e.target as FormSwitch).checked;
        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { email_opt_in_required }, this.amplitude_callsite);
    }

    async onChangeCheckIp(e: CustomEvent) {
        const check_ip_address = (e.target as FormSwitch).checked;
        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { check_ip_address }, this.amplitude_callsite);
    }

    private async onUpdateTargetCategoryDescription(category_id: string | undefined) {
        // Save current description if we were editing one
        if (isDefined(this.editingCategoryDescription)) {
            const old_text = this.categoryDescriptionElement?.getInnerText() ?? '';
            const categoryDescriptions = [...(this.election?.category_descriptions ?? [])];

            const existingIndex = categoryDescriptions.findIndex((cd) => cd.category_id === this.editingCategoryDescription);

            if (existingIndex >= 0) {
                categoryDescriptions[existingIndex].description = old_text;
            } else {
                categoryDescriptions.push({
                    category_id: this.editingCategoryDescription,
                    description: old_text
                });
            }

            this.election = await topicData.fetchAndUpdateElection(
                this.election?._id?.$oid,
                { category_descriptions: categoryDescriptions },
                this.amplitude_callsite
            );
        }

        // Switch to new category
        this.editingCategoryDescription = category_id;

        if (isDefined(this.editingCategoryDescription)) {
            const categoryDescription = this.election?.category_descriptions?.find(
                (cd) => cd.category_id === category_id
            );

            await this.updateComplete;
            this.categoryDescriptionElement?.setInnerText(categoryDescription?.description ?? '');
        }
    }

    closeElectionModal() {
        this.electionModal.close();
    }

    private downloadVotes() {
        let content = '';

        content +=
            '"Vote Id","Category Name","Category ID","Point Id","Point Name",Email,"Ip Address","Created At","Updated At"\n';

        for (const ballot of this.ballots) {
            for (const category of ballot.ballot) {
                const category_name = topicData.categories.find((c) => c._id?.$oid === category.category_id.$oid)?.name;
                for (const place of category.selected) {
                    content += `${escapeCsvValue(ballot._id.$oid)},${escapeCsvValue(category_name)},${escapeCsvValue(category.category_id.$oid)},${escapeCsvValue(place.point_id.$oid)},${escapeCsvValue(place.point_name)},${escapeCsvValue(ballot.email)},${escapeCsvValue(ballot.ip_address)},${escapeCsvValue(ballot.created_at)},${escapeCsvValue(ballot.updated_at)}\n`;
                }
            }
        }


        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const filename = `election_votes_${this.election?._id?.$oid ?? '_'}_${new Date().toISOString()}.csv`;

        const blob = new Blob([content], {
            type: 'text/plain;charset=utf-8',
        });

        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';

        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    private async updateEligibleCategorySetting(category: TopicCategory, isEligible: boolean) {
        let excludedCategories = this.election?.excluded_categories ?? [];
        if (isEligible) {
            excludedCategories = excludedCategories.filter((c) => c.$oid !== category._id?.$oid);
        } else if (isDefined(category._id) && !excludedCategories.some((ec) => ec.$oid === category._id?.$oid)) {
            excludedCategories.push({ $oid: category._id.$oid });
        }
        this.election = await topicData.fetchAndUpdateElection(this.election?._id?.$oid, { excluded_categories: excludedCategories }, this.amplitude_callsite);
    }

    downloadTotalVotes() {
        const votes = this.election_standings;
        let content = '';
        content += '"Category Name", "Category ID", "Point Name", "Point ID", Votes\n';
        for (const vote of votes) {
            const category_name = topicData.categories.find((c) => c._id?.$oid === vote.category_id)?.name;
            for (const place of vote.leaders) {
                const place_name = pointCache.allPoints.find((p) => p._id?.$oid === place.point_id)?.name;
                content += `${escapeCsvValue(category_name)},${escapeCsvValue(vote.category_id)},${escapeCsvValue(place_name)},${escapeCsvValue(place.point_id)},${place.votes}\n`;
            }
        }

        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const filename = `election_votes_${this.election?._id?.$oid ?? '_'}_${new Date().toISOString()}.csv`;

        const blob = new Blob([content], {
            type: 'text/plain;charset=utf-8',
        });

        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';

        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    getUniqueParticipants() {
        const participants: VoterDetail[] = [];
            for (const ballot of this.ballots) {
                const existing = participants.find(
                    (l) => l.email === ballot.email
                );
                if (isDefined(existing)) {
                    existing.num_votes++;
                    existing.opt_in = existing.opt_in ?? ballot.email_opt_in;
                    existing.opt_in_language = existing.opt_in_language ?? ballot.email_opt_in_language;
                } else {
                    participants.push({
                        email: ballot.email,
                        num_votes: 1,
                        opt_in: ballot.email_opt_in,
                        opt_in_language: ballot.email_opt_in_language
                    });
                }

            }
        return participants;
    }

    downloadParticipantDetails() {
        const participants = this.uniqueParticipants;
        let content = '';
        content += 'Email, Votes, Opt In, Opt In Language\n';
        for (const participant of participants) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            content += `${escapeCsvValue(participant.email)},${participant.num_votes},${participant.opt_in ? 'true' : 'false'},${participant.opt_in_language}\n`;
        }

        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const filename = `election_participants_${this.election?._id?.$oid ?? '_'}_${new Date().toISOString()}.csv`;

        const blob = new Blob([content], {
            type: 'text/plain;charset=utf-8',
        });

        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';

        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    private async openPreviewForm() {
        this.electionFormPreviewElement.refreshForPreview = true;
        this.electionModal.show();
    }

    openUpgradeDialog(e: CustomEvent) {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.Election,
                },
            })
        );
    }

    async getMapLink() {
        const link = topicData.swapUrlForOrgUrl(topicData.topic?.cc_read_link ?? '');
        if (!link) {
            getMainPage().errorToastElement.showToastAndThrow('There was an error getting the map link');
            return;
        }
        await navigator.clipboard.writeText(link);
        getMainPage().errorToastElement.showToast('Map link copied to clipboard');
    }

    async getNominationsLink() {
        const link = topicData.swapUrlForOrgUrl(this.election?.link_set?.nomination_link ?? '');
        if (!isDefined(link)) {
            getMainPage().errorToastElement.showToastAndThrow('There was an error getting the nominations link');
            return;
        }
        await navigator.clipboard.writeText(link);
        getMainPage().errorToastElement.showToast('Nominations link copied to clipboard');
    }

    async getBallotLink() {
        const link = topicData.swapUrlForOrgUrl(this.election?.link_set?.ballot_link ?? '');
        if (!isDefined(link)) {
            getMainPage().errorToastElement.showToastAndThrow('There was an error getting the voting link');
            return;
        }
        await navigator.clipboard.writeText(link);
        getMainPage().errorToastElement.showToast('Voting link copied to clipboard');
    }

    private async loadPaymentData() {
        if (!topicData.topic?.organization_id || !isDefined(organizationInfo.organization?._id)) {
            console.error('No organization ID found for topic.');
            return;
        }
        try {
            [this.stripeInfo, this.purchaseHistory] = await Promise.all([
                loadStripeStatus(organizationInfo.organization?._id.$oid ?? ''),
                getOrganizationPurchaseHistory(organizationInfo.organization?._id.$oid ?? '', this.election?._id?.$oid ?? '', topicData.topicKey)
            ]);
        } catch (error) {
            getMainPage().errorToastElement.showToast('Failed to load purchases information.');
        }
    }

    async setEditedProduct(e: CustomEvent) {
        this.isEditingProduct = true;
        this.requestUpdate();
        await delay(100);
        const product = e.detail.product;
        if (this.createProductComponent) {
            await this.createProductComponent.setExistingProduct(product);
        }
    }

    renderProductCenter() {
        if (!(organizationInfo.organization?.is_administrator ?? false)) {
            return html`
                <settings-card header="Payment Settings">
                    <p class="info-paragraph">You do not have the required permissions to view or manage products and purchases. Please contact your organization administrator for access.</p>
                </settings-card>
            `;
        }

        return html`
            <settings-card header="Create Products">
                <p class="info-paragraph">Create and manage charges for submitting ballots in this election.</p>
                    ${this.ballotProducts.filter((p) => p.status === OrganizationProductStatus.Active).length === 0 || this.isEditingProduct ? html`
                        <create-product-component
                        id="createProductComponent"
                            .payment_target_type=${PaymentTargetType.Ballot}
                            .stripeInfo=${this.stripeInfo}
                            .payment_target_entity_id=${this.election?._id.$oid ?? ''}
                            @product-updated="${this.getBallotProducts}"
                        >
                        </create-product-component>
                    ` : html`
                        <p class="info-paragraph">You can only have one active product at a time for ballot payments. Please archiver your existing active product before creating a new one.</p>
                    `}
            </settings-card>
            <settings-card header="Existing Products and Prices">
                <p class="info-paragraph">View and manage existing products and prices</p>
                ${this.ballotProducts.length > 0 ? html`
                    <existing-products-component
                        .data="${this.ballotProducts}"
                        .payment_target_type=${PaymentTargetType.Ballot}
                        .purchases=${this.purchaseHistory?.transactions ?? []}
                        @set-edited-product="${this.setEditedProduct}"
                        @update-products="${this.getBallotProducts}"
                    >
                    </existing-products-component>
                ` : html`
                    <p>No products found.</p>
                `}
            </settings-card>
            <settings-card header="Completed Purchases">
                <p class="info-paragraph">View all completed purchases made by users.</p>
                ${!isDefined(this.purchaseHistory) || this.purchaseHistory.transactions.length > 0 ? html`
                    <completed-purchases-component
                        .data=${this.purchaseHistory}
                    ></completed-purchases-component>
                ` : html`
                    <p>No completed purchases found.</p>
                `}
            </settings-card>
        `;
    }

    render() {
        if (!topicData.isReady || !pointCache.isReady || !this.election) {
            return html`<loading-element loadingString="Getting Best Of Data..."></loading-element>`;
        }
        return html`
            <div class="wrapper">
                <div class="header-title-and-buttons" style="background-color: ${topicData.backgroundColor}; color: ${topicData.textColor}">
                    <div class="left-header-content">
                        <span class="additional checkboxHolder" style="margin-left: 0.2rem;">
                            <form-switch
                                id="liveSwitch"
                                name="liveSwitch"
                                .colored=${true}
                                style="flex: unset;"
                                ?checked=${this.election?.nomination_is_live}
                                ?disabled=${!featuresInfo.electionEnabled}
                                @change="${this.onChangeNominationsStatus}">
                            </form-switch>
                            Nominations are Live
                        </span>
                        <span class="additional checkboxHolder" style="margin-left: 0.2rem;">
                            <form-switch
                                id="liveSwitch"
                                name="liveSwitch"
                                .colored=${true}
                                style="flex: unset;"
                                ?checked=${this.election?.active}
                                ?disabled=${!featuresInfo.electionEnabled}
                                @change="${this.onChangeActiveStatus}">
                            </form-switch>
                            Voting is Live
                        </span>
                        <span class="additional checkboxHolder" style="margin-left: 0.2rem;">
                            <form-switch
                                id="liveSwitch"
                                name="liveSwitch"
                                .colored=${true}
                                style="flex: unset;"
                                ?checked=${this.election?.show_leaders !== 0 && this.election?.show_leaders}
                                ?disabled=${!featuresInfo.electionEnabled}
                                @change="${this.toggleShowLeaders}">
                            </form-switch>
                            Results are Public
                        </span>
                    </div>
                    <div class="middle-header-content">
                        ${this.election?.name}
                    </div>

                    <div class="right-header-content">
                        <form-button size="xs" variant="outline" @click="${this.openPreviewForm}" .textColor=${topicData.textColor}>
                            Preview Ballot
                        </form-button>
                        <form-button size="xs" variant="outline" @click="${this.getNominationsLink}" .textColor=${topicData.textColor}>
                            Nomination Link
                        </form-button>
                        <form-button size="xs" variant="outline" @click="${this.getBallotLink}" .textColor=${topicData.textColor}>
                            Voting/Ballot Link
                        </form-button>
                        <form-button size="xs" variant="outline" .textColor=${topicData.textColor} @click="${this.getMapLink}">
                            Map Link
                        </form-button>
                    </div>
                </div>
                <div class="tabs">
                    <tabs-component
                        .options=${this.options}
                        .tabIndex=${this.selected_tab === 'Setup' ? 0 : this.selected_tab === 'Results' ? 1 : this.selected_tab === 'Voters' ? 2 : this.selected_tab === 'Nominations' ? 3 : this.selected_tab === 'Payment' ? 4 : this.selected_tab === 'Services' ? 5 : 0}
                        @on-select-tab=${this.handleChangeTab}
                    ></tabs-component>
                </div>
                <div class="tab__content">
                    ${this.selected_tab === 'Setup' ? this.renderSetup() : ''}
                    ${this.selected_tab === 'Results' ? this.renderResults() : ''}
                    ${this.selected_tab === 'Voters' ? this.renderVoters() : ''}
                    ${this.selected_tab === 'Nominations' ? this.renderNominations() : ''}
                    ${this.selected_tab === 'Payment' ? this.renderProductCenter() : ''}
                    ${this.selected_tab === 'Services' ? this.renderServices() : ''}
                </div>
            </div>
                        <mwc-dialog
                id="electionModal"
                class="dialog"
                hideActions
                scrimClickAction=""
                escapeKeyAction=""
            >
            <modal-dialog size="md" @close="${this.closeElectionModal}">
                    <election-form-component
                        id="electionFormPreview"
                        slot="body"
                        .election=${this.election}
                        .refreshForPreview=${false}
                        ?previewMode="${true}"
                        @on-close="${this.closeElectionModal}"
                    ></election-form-component>
                </modal-dialog>
            </mwc-dialog>
        `;
    }

    protected renderSetup() {
        const included_categories = topicData.categories?.filter((c) => c._id && this.election?.excluded_categories.map((ec) => ec.$oid).includes(c._id.$oid) !== true);
        return html`
            <settings-card header="Registration Form Content">
                <div class="date-container">
                    <vaadin-date-picker ?disabled="${!featuresInfo.electionEnabled}"
                        required
                        id="startDate"
                        class="input__date"
                        label="Voting Start Date"
                        placeholder="Start Date"
                        value="${this.electionStartDateLocal ?? ''}"
                        @change="${this.onChangeStartDate}"
                    ></vaadin-date-picker>
                    <vaadin-date-picker ?disabled="${!featuresInfo.electionEnabled}"
                        required
                        id="endDate"
                        class="input__date"
                        label="Voting End Date"
                        placeholder="End Date"
                        value="${this.electionEndDateLocal ?? ''}"
                        @change="${this.onChangeEndDate}"
                    ></vaadin-date-picker>
                </div>
                <proxi-textfield
                    ?disabled="${!featuresInfo.electionEnabled}"
                    outlined
                    required
                    class="input__election-title"
                    id="election-title"
                    type="text"
                    label="Election or Competition Title"
                    placeholder="Title (e.g. Best of 2024, Top 10, etc)"
                    value="${this.election?.name ?? ''}"
                    @change="${this.onChangeElectionName}"
                >
                </proxi-textfield>
            </settings-card>
            <settings-card header="Voting Form Content">
                <h4 class="sub-title">Description and Terms</h4>
                <p class="info-paragraph">Describe the competition and what users can expect and link to any applicable terms and conditions. This will be displayed on the voting form.</p>
                <rich-text-editor showSave id="description" .allowEmbed=${false} style="margin-top: 0.25rem; width:100%" @save="${this.onChangeElectionDescription}">
                </rich-text-editor>
                <h4 class="sub-title">Button CTAs</h4>
                <proxi-textfield
                    outlined ?disabled="${!featuresInfo.electionEnabled}"
                    required
                    class="input__election-title"
                    type="text"
                    label="Vote Button Text"
                    placeholder="Vote Now!, Pick Me!, etc"
                    value="${this.election?.point_button_text ?? ''}"
                    @change="${this.onChangePointButtonText}"
                >
                </proxi-textfield>
                <proxi-textfield
                    outlined ?disabled="${!featuresInfo.electionEnabled}"
                    required
                    class="input__election-title"
                    id="checkin-code"
                    type="text"
                    label="Submit Button Text"
                    placeholder="Vote Now!, Submit Ballot, etc"
                    value="${this.election?.submit_button_text ?? ''}"
                    @change="${this.onChangeSubmitButtonText}"
                ></proxi-textfield>
            </settings-card>
            <settings-card header="Category Settings">
                <h4 class="sub-title">Eligible Voting Categories</h4>
                    <form-tooltip
                        tipwidth="180px"
                        leftPadding="30%"
                        message="Limit which categories are part of the voting.">
                            <mwc-icon>info_outline</mwc-icon>
                    </form-tooltip>
                </h4>
                <div class="category__wrapper">
                    ${topicData.categories?.map((category) => {
                        const eligible = isDefined(category._id?.$oid) && this.election?.excluded_categories.map((c) => c.$oid).includes(category._id?.$oid ?? '') === false;
                        return html`
                        <div class="category__item"
                            style="${eligible ? `background-color: ${featuresInfo.electionEnabled ? 'var(--main-primary-color)' : 'var(--main-moon-color)'}; color: white` : `background-color: #fff; border: 2px solid transparent;border: 1px solid var(--main-primary-color);`}"
                            @click=${async () => await this.updateEligibleCategorySetting(category, !eligible)}
                            >
                            ${eligible ? html`<img class="menuPng" src="${category.icon}" />` : html`<mwc-icon style="margin:0; --mdc-icon-size: 16px;">
                                visibility_off</mwc-icon>`}
                            ${category.name}
                            <div class="category__icon">${eligible ? String.fromCodePoint(0x2D) : String.fromCodePoint(0x2B)}</div>
                        </div>
                    `;})}
                </div>
                <h4 class="sub-title">Category Descriptions</h4>
                <p class="info-paragraph">You can add a description to each category to help users understand what they are voting on.</p>
                <div class="category__wrapper">
                    ${included_categories.map((category) => {
                        const editingThisCategory = this.editingCategoryDescription === category._id?.$oid;
                        return html`
                        <div class="category__item"
                            style="${editingThisCategory ? `background-color: ${featuresInfo.electionEnabled ? 'var(--main-primary-color)' : 'var(--main-moon-color)'}; color: white` : `background-color: #fff; border: 2px solid transparent;border: 1px solid var(--main-primary-color);`}"
                            @click=${async () => await this.onUpdateTargetCategoryDescription(category._id?.$oid ?? '')}
                            >
                            ${topicData.categories.find((c) => c._id?.$oid === category._id?.$oid)?.name}
                        </div>
                    `;})}
                </div>
                <rich-text-editor
                    showSave
                    id="categoryDescriptionEditor"
                    .allowEmbed=${false}
                    style="width:100%;"
                    @save="${async () => await this.onUpdateTargetCategoryDescription(this.editingCategoryDescription)}"
                ></rich-text-editor>
            </settings-card>
            <settings-card header="Security and Submission Options">
                <h4 class="sub-title">Single or Multi-Vote Per Category</h4>
                <div class="checkbox-wrapper">
                    <input ?disabled="${!featuresInfo?.electionEnabled}"
                        id="allowMultiSelect"
                        name="allowMultiSelect"
                        type="checkbox"
                        style="accent-color: var(--main-primary-color)"
                        ?checked="${!(this.election?.multi_select ?? false)}"
                        @change="${this.onChangeMultiSelect}"
                        >
                    <span>Restrict users to one vote per category</span>
                </div>
                <h4 class="sub-title">Frequency of Voting</h4>
                <mwc-select ?disabled="${!featuresInfo.electionEnabled}"
                    outlined
                    class="input__election-title"
                    id="election-title"
                    label="Permitted Voting Frequency"
                    value="${this.election?.frequency_recurrance ?? 'daily'}"
                    @change="${this.onChangeFrequencyRecurrence}"
                >
                    <mwc-list-item value="daily">Daily</mwc-list-item>
                    <mwc-list-item value="weekly">Weekly</mwc-list-item>
                    <mwc-list-item value="monthly">Monthly</mwc-list-item>
                    <mwc-list-item value="once">Once</mwc-list-item>
                    <mwc-list-item value="unlimited">Unlimited (Not Recommended)</mwc-list-item>
                </mwc-select>
                <h4 class="sub-title">Email Collection and Verification</h4>
                <div class="checkbox-wrapper">
                    <input ?disabled="${!featuresInfo.electionEnabled}"
                        id="verifyEmail"
                        name="verifyEmail"
                        type="checkbox"
                        style="accent-color: var(--main-primary-color"
                        ?checked="${this.election?.verify_email}"
                        @change="${this.onChangeVerifyEmail}"
                        >
                    <span>Voters required to verify their email before voting</span>
                </div>
                <div class="checkbox-wrapper">
                    <input ?disabled="${!featuresInfo.electionEnabled}"
                        id="requireOptIn"
                        name="requireOptIn"
                        type="checkbox"
                        style="accent-color: var(--main-primary-color)"
                        ?checked="${this.election?.email_opt_in_required}"
                        @change="${this.onChangeRequireOptIn}"
                        >
                    <span>Users are required to opt in to the below language before voting</span>
                </div>
                <p class="info-paragraph">If you do not want an opt in checkbox at all, leave the text field blank.</p>
                <proxi-textfield ?disabled="${!featuresInfo.electionEnabled}"
                    outlined
                    class="input__election-title"
                    id="election-title"
                    type="text"
                    ?required="${this.election?.email_opt_in_required}"
                    label="Email Address Opt In Language ${this.election?.email_opt_in_required === true ? '' : 'Optional'}"
                    placeholder="By voting you acknowledge that we might send you follow up emails about the election and our organization"
                    value="${this.election?.email_terms ?? ''}"
                    @change="${this.onChangeElectionEmailTerms}"
                >
                </proxi-textfield>
                <h4 class="sub-title">Security</h4>
                <div class="checkbox-wrapper">
                    <input ?disabled="${!featuresInfo?.electionEnabled}"
                        id="checkIp"
                        name="checkIp"
                        type="checkbox"
                        style="accent-color: var(--main-primary-color)"
                        ?checked="${this.election?.check_ip_address}"
                        @change="${this.onChangeCheckIp}"
                        >
                    <span>Use Voter IP Addresses to Prevent Spam</span>
                </div>
            </settings-card>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    protected renderResults() {
        return html`
            <settings-card header="Overall Stats">

                <div class="stats-card-container">
                    <div class="stats-card third">
                        <div class="stats-card__value">${this.ballots.length}</div>
                        <div class="stats-card__label">Ballots</div>
                    </div>
                    <div class="stats-card third">
                        <div class="stats-card__value">${this.uniqueParticipants.length ?? 0}</div>
                        <div class="stats-card__label">Participants</div>
                    </div>
                    <div class="stats-card third">
                        <div class="stats-card__value">${this.ballots.map((b) => b.ballot.length).reduce((a, b) => a + b, 0)}</div>
                        <div class="stats-card__label">Total Votes</div>
                    </div>
                </div>
            </settings-card>
            <settings-card header="Show Results Settings">
                <h4 class="sub-title">Options for Displaying Current Winners</h4>
                <proxi-selectfield ?disabled="${!featuresInfo.electionEnabled}"
                    outlined
                    class="input__election-title"
                    id="election-show-leaders"
                    label="Show Leaders/Winners to Public"
                    value="${this.election?.show_leaders ?? 'None'}"
                    @change="${this.onChangeShowLeaders}"
                >
                    <mwc-list-item value="None">None</mwc-list-item>
                    <mwc-list-item value="3">Top 3</mwc-list-item>
                    <mwc-list-item value="2">Top 2</mwc-list-item>
                    <mwc-list-item value="1">Only 1st Place</mwc-list-item>
                </proxi-selectfield>
                <br><br>
                <p>Are you interested in ordering custom trophies, certificates, or door stickers for the winners?
                    <span @click="${() => openChatWindow(this.amplitude_callsite, 'I am interested in ordering custom trophies, certificates, or door stickers for the winners of my context')}" style="color: var(--main-primary-color); cursor: pointer; font-weight: bold;text-decoration: underline;">
                        Chat with us</span> to learn more!</p>
            </settings-card>
            <settings-card header="Standings by Category">
                <p class="info-paragraph">Click on a category to see the current standings. You can also download the data for further analysis.</p>
                ${this.ballots.length === 0 ? html`
                    <p> Your standings will appear below once the first ballot has been cast. </p>
                ` : ''}
                <div class="category__wrapper">
                    ${topicData.categories?.map((category) => {
                        const eligible = isDefined(category._id?.$oid) && this.election?.excluded_categories.map((c) => c.$oid).includes(category._id?.$oid ?? '') === false;
                        const isCurrentlySelectied = this.standings_category_filter === (category._id?.$oid ?? '');
                        return html`
                            ${!eligible ? '' : html`
                                <div class="category__item"
                                    style="${isCurrentlySelectied ? `background-color: ${featuresInfo.electionEnabled ? 'var(--main-primary-color)' : 'var(--main-moon-color)'}; color: white` : `background-color: #fff; border: 2px solid transparent;border: 1px solid var(--main-primary-color);`}"
                                    @click=${(e:any) => {this.standings_category_filter = category._id?.$oid ?? '';}}
                                    >
                                    <img class="menuPng" src="${category.icon}" />
                                    ${category.name}
                                </div>
                            `}
                            `;
                        })
                    }
                </div>
                <div class="standings-table-container">
                    ${this.election_standings.filter((s) => !this.standings_category_filter || s.category_id === this.standings_category_filter).map( s => html`
                        ${s.leaders.map( l => html`
                            <div class="standings-row">
                                <div class="standings-row__name">${pointCache.allPoints.find((p) => p._id?.$oid === l.point_id)?.name}</div>
                                <div class="standings-row__votes">${l.votes}</div>
                            </div>
                        `)}
                    `)}
                </div>
            </settings-card>
            <settings-card header="Downloads">
                <div class="download__options">
                    <mwc-button
                        raised
                        class="view_participants_progress not-full-width"
                        @click="${this.downloadVotes}"
                    >   <mwc-icon>download</mwc-icon>
                        Download All Votes
                    </mwc-button>
                    <mwc-button
                        raised
                        class="view_participants_progress not-full-width"
                        @click="${this.downloadTotalVotes}"
                    >   <mwc-icon>download</mwc-icon>
                        Download Leaderboard
                    </mwc-button>
                    <mwc-button
                        raised
                        class="view_participants_progress not-full-width"
                        @click="${this.downloadParticipantDetails}"
                    >   <mwc-icon>download</mwc-icon>
                        Download Participants
                    </mwc-button>
                </div>
            </div>
            </settings-card>
        `;
    }

    sortClickHandler(sortBy: string) {
        if (this.sortBy === sortBy) {
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortBy = sortBy;
            this.sortOrder = 'asc';
        }

        this.sortedNominees = pointCache.getSortedPoints(undefined, true).sort((a, b) => {
            let comparison = 0;
            if (sortBy === 'name') {
                comparison = (a.name ?? '').localeCompare(b.name ?? '');
            } else if (sortBy === 'category') {
                comparison = (topicData.categories.find((c) => c._id?.$oid === a.topic_category_id?.$oid)?.name ?? '').localeCompare(topicData.categories.find((c) => c._id?.$oid === b.topic_category_id?.$oid)?.name ?? '');
            } else if (sortBy === 'likes') {
                comparison = (a.likes?.length ?? 0) - (b.likes?.length ?? 0);
            } else if (sortBy === 'created') {
                comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            } else if (sortBy === 'updated') {
                comparison = new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
            }

            return this.sortOrder === 'asc' ? comparison : -comparison;
        });
    }

    votersSortClickHandler(sortBy: string) {
        if (this.votersSortBy === sortBy) {
            this.votersSortOrder = this.votersSortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.votersSortBy = sortBy;
            this.votersSortOrder = 'asc';
        }
    }

    renderNominations() {
        return html`
            <settings-card header="Nominees (${pointCache.points.length})" .maxWidth=${'100%'}>
                <p class="info-paragraph">Additional Options Coming Soon</p>
                <br>
                <div class="modalContent">
                   <div class="challenges-wrapper">
                        <div class="header-row">
                            <div class="header-cell text primary" @click="${() => this.sortClickHandler('name')}">Name ${this.sortBy === 'name' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            <div class="header-cell text" @click="${() => this.sortClickHandler('category')}">Category${this.sortBy === 'category' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            <div class="header-cell number" @click="${() => this.sortClickHandler('likes')}">Upvotes ${this.sortBy === 'likes' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                        </div>
                        ${this.sortedNominees.map((point) => html`
                            <div class="challenge-row">
                                <div class="user data-cell text primary">${point.name ?? ''}</div>
                                <div class="email data-cell text">
                                    ${topicData.categories.find((c) => c._id?.$oid === point.topic_category_id?.$oid)?.name ?? ''}
                                </div>
                                <div class="phon data-cell number">${point.likes?.length}</div>
                            </div>
                        `)}
                    </div>
                </div>
            </settings-card>
        `;
    }

    get groupedBallotsByVoter() {
        const grouped: {[email: string]: { total_votes: number; ip_addresses: string[]; total_ballots: number; paid_ids: string[] }} = {};
        const payments: {[email: string]: string[]} = {};

        this.purchaseHistory?.transactions.forEach((transaction) => {
            if (transaction.status === PurchaseStatus.Completed) {
                const email = transaction.user_email.toLowerCase();
                if (!isDefined(payments[email])) {
                    payments[email] = [];
                }
                payments[email].push(transaction.purchase_id?.$oid ?? '');
            }
        });

        this.ballots.forEach((ballot) => {
            const email = ballot.email.toLowerCase();
            if (!isDefined(grouped[email])) {
                grouped[email] = {
                    total_votes: 0,
                    ip_addresses: [],
                    total_ballots: 0,
                    paid_ids: []
                };
            }

            grouped[email].total_ballots += 1;
            grouped[email].total_votes += ballot.ballot.length;

            if (ballot.ip_address && !grouped[email].ip_addresses.includes(ballot.ip_address)) {
                grouped[email].ip_addresses.push(ballot.ip_address);
            }

            // Check if there are actual payment IDs for this email
            if (isDefined(payments[email]) && payments[email].length > 0) {
                grouped[email].paid_ids = payments[email];
            }
        });

        return grouped;
    }

    renderVoters() {
        return html`
            <settings-card header="Voters">
                <div class="challenges-wrapper">
                    <div class="header-row">
                        <div class="header-cell text primary" @click="${() => this.sortClickHandler('email')}">Email ${this.votersSortBy === 'email' ? html`<mwc-icon>${this.votersSortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                        <div class="header-cell number" @click="${() => this.sortClickHandler('ballots')}">Ballots Submitted ${this.votersSortBy === 'ballots' ? html`<mwc-icon>${this.votersSortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                        <div class="header-cell number" @click="${() => this.sortClickHandler('votes')}">Votes Submitted ${this.votersSortBy === 'votes' ? html`<mwc-icon>${this.votersSortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                        <div class="header-cell number">IP Addresses</div>
                        ${this.ballotProducts.length > 0 ? html`<div class="header-cell number" @click="${() => this.sortClickHandler('paid')}">Paid Status ${this.votersSortBy === 'paid' ? html`<mwc-icon>${this.votersSortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>` : ''}
                    </div>
                    ${Object.entries(this.groupedBallotsByVoter).sort((a, b) => {
                        let comparison = 0;
                        if (this.votersSortBy === 'email') {
                            comparison = a[0].localeCompare(b[0]);
                        } else if (this.votersSortBy === 'ballots') {
                            comparison = a[1].total_ballots - b[1].total_ballots;
                        } else if (this.votersSortBy === 'votes') {
                            comparison = a[1].total_votes - b[1].total_votes;
                        } else if (this.votersSortBy === 'paid') {
                            const aPaid = a[1].paid_ids.length > 0 ? 1 : 0;
                            const bPaid = b[1].paid_ids.length > 0 ? 1 : 0;
                            comparison = aPaid - bPaid;
                        }
                        return this.votersSortOrder === 'asc' ? comparison : -comparison;
                    }).map(([email, data]) => html`
                        <div class="challenge-row">
                            <div class="email data-cell text primary">
                                <span style="margin-right: 5px;">
                                    ${email}
                                </span>
                            </div>
                            <div class="visits data-cell number">${data.total_ballots}</div>
                            <div class="checkin data-cell number">${data.total_votes}</div>
                            <div class="point data-cell number">${data.ip_addresses.join(', ')}</div>
                            ${this.ballotProducts.length > 0 ? html`
                                <div class="point data-cell number">
                                    ${data.paid_ids.length > 0 ? html`<mwc-icon style="color: green;">check_circle</mwc-icon>` : html`<mwc-icon style="color: red;">cancel</mwc-icon>`}
                                </div>
                            ` : ''}
                        </div>
                    `)}
                </div>
            </settings-card>
        `;
    }

    renderServices() {
        const serviceConfig = {
            context: 'competition' as const,
            setupPrice: '$250',
            contextName: 'competition',
            contextDescription: 'competitions and voting campaigns'
        };

        return html`
            <professional-services-component
                .config=${serviceConfig}
                .amplitudeCallsite=${this.amplitude_callsite}>
            </professional-services-component>
        `;
    }

    static get styles() {
        return css`
            .wrapper {
                display: flex;
                flex-direction: column;
                overflow: hidden auto;
                box-sizing: border-box;
                width: 100%;
                height: 100%
            }

            .header-title-and-buttons {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                padding: 10px;
                font-size: 18px;
                background-color: var(--theme-border-color);
                border-bottom: 1px solid var(--theme-border-color);
            }

            .left-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap: 5px;
                flex: 1;
                flex-wrap: wrap;
            }

            .checkboxHolder {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .right-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap:5px;
                justify-content: flex-end;
                flex: 1;
                flex-wrap: wrap;
            }
            .modalContent {
                position: relative;
                width: 100%;
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .middle-header-content {
                flex: 1;
                font-size: 1rem;
                text-align:center;
            }

            .header-title-and-buttons h4 {
                margin: 5px 0;
            }

            .tabs {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0 20px;
                background-color: var(--theme-white-color);
                border-bottom: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
            }

            .tab__content {
                padding: 20px;
                display: flex;
                gap: 20px;
                flex: 1;
                overflow: hidden auto;
                justify-content: flex-start;
                align-items: stretch;
                background-color: var(--theme-border-color);
                flex-direction: column;
            }

            /* width */
            .tab__content::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .tab__content::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .tab__content::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .manage-participants-and-results {
                display: flex;
                flex-direction: row;
                gap: 10px;
                height: 100%;
            }


            .category__wrapper {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                margin-top: 0.5rem;
            }

            .category__item {
                position: relative;
                user-select: none;
                cursor: pointer;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 3px 14px 3px 10px;
                border-radius: 1.25rem;
                margin-right: 6px;
                margin-bottom: 4px;
                font-size: 10px;
                border: 1px solid var(--main-primary-color);
            }

            .category__icon {
                border-radius: 999px;
                width: 1rem;
                height: 1rem;
                font-size: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                position: absolute;
                top: -5px;
                right: -5px;
                background: var(--theme-white-color);
                border: 1px solid var(--main-primary-color);
                color: var(--main-primary-color);
            }

            .disabled {
                background-color: var(--theme-border-color);
                color: var(--theme-input-color);
            }

            .menuPng {
                margin-right: 0.25rem;
                height: 15px;
                margin-top: auto;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                margin-bottom: 0px;
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
            }

            h4.sub-title {
                font-weight: 600;
                font-size: 12px;
                line-height: 100%;
                color: #000000;
                text-align: left;
                margin: 15px 0 5px;
                display: flex;
                align-items: center;
                flex-direction: row;
            }

            .election-details__tabs {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 20px;
                border: 1px solid var(--main-primary-color);
            }

            .election-details__tab {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                line-height: 150%;
                color: var(--main-primary-color);
                background: transparent;
                padding: 5px 10px;
                cursor: pointer;
            }

            .election-details__tab.right {
                border-radius: 0px 20px 20px 0px;
            }

            .election-details__tab.left {
                border-radius: 20px 0px 0px 20px;
            }

            .election-details__tab.active {
                background: var(--main-primary-color);
                color: var(--theme-white-color);
            }

            .tab-icon {
                width: 12px;
                height: 12px;
                margin-right: 5px;
            }

            .tab-icon.small {
                width: 12px;
                height: 12px;
            }

            .input__election-title {
                width: 100%;
                margin-top: 15px;
            }

            p {
                margin-top: 5px;
                margin-bottom: 5px;
            }

            .addon__footer {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                flex-wrap: wrap;
                align-items: center;
                padding: 0 20px;
                gap: 10px;
            }

            .challeng-save-button {
                --mdc-theme-primary: var(--main-success-color);
                --mdc-typography-button-text-transform: case;
                margin: 10px 0;
            }

            .date-container {
                width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
            }

            .input__date {
                width: 48%;
            }

            .checkbox-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 2px;
                line-height: 120%;
                color: rgb(16, 44, 61);
                margin-top: 5px;
            }

            .checkbox-wrapper span {
                margin-bottom: 4px;
            }

            .edit__textfield {
                width: 100%;
                margin-top: 15px;
            }

            h4 {
                margin-bottom: 0px;
                color: var(--theme-input-color);
            }

            .awards-wrapper {
                margin: 20px 0;
            }

            .button__wrapper {
                width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: flex-end;
                margin: 15px 0;
            }

            .hidden {
                display: none;
            }

            .view_participants_progress {
                border: 1px solid var(--main-primary-color);
                --mdc-theme-on-primary: var(--main-primary-color);
                --mdc-theme-primary: transparent;
                --mdc-button-height: 50px;
                --mdc-typography-button-font-size: 12px;
                --mdc-typography-button-text-transform: capitalize;
            }

            .not-full-width {
                width: unset;
            }

            .redButton {
                --mdc-theme-primary: var(--main-invalid-color);
            }


            .redText {
                color: var(--main-invalid-color);
            }

            .election-save-button {
                font-size: 16px;
                border-radius: 8px;
                --mdc-theme-on-primary: var(--theme-white-color);
                --mdc-typography-button-text-transform: capitalize;
                --mdc-theme-primary: #5EA667;
            }

            .greyed-out {
                --mdc-theme-primary: grey;
            }

            .setting-switch {
                align-items: center;
                display: flex;
                gap: 8px;
                margin: 0.5rem auto;
            }

            .switch-label {
                text-align: left;
            }

            .switch-label.left {
                text-align: right
            }

            .stats-card-container {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                box-sizing: border-box;
                flex-wrap: wrap;
            }

            .standings-table-container {
                width: 100%
            }

            .standings-row__votes {
                flex: 1
            }

            .standings-row__name {
                flex: 2
            }

            .download__options {
                width: 100%;
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-start;
                gap: 10px;
                flex-wrap: wrap;
            }

            .standings-row {
                display: flex;
                gap: 10px;
                justify-content: flex-start;
                border: 1px black solid;
                width: 100%;
                padding: 5px 2px;
            }

            .stats-card {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                border: 1px solid #cac4d0;
                border-radius: 12px;
                box-sizing: border-box;
                height: 50px;
            }

            .stats-card.third {
                width: calc(33.33% - 12px);
                margin: 15px 5px;
                justify-content: space-between;
            }

            .stats-card__value {
                font-size: 24px;
                line-height: 130%;
                color: #000000;
                font-weight: 700;
                maring-right: 4px;
            }

            .stats-card__label {
                line-height: 125%;
                color: #1d1b20;
            }

            .awards-earned {
                width: 100%;
                text-align: center;
                font-weight: 700;
                font-size: 16px;
                line-height: 150%;
                color: #000000;
                margin: 15px 0;
            }

            .awards__wrapper {
                max-width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: flex-start;
                overflow-x: auto;
            }

            .awards__card {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 0px 5px;
                gap: 5px;
                flex: 1;
            }

            .award-icon {
                width: 37px;
                height: 37px;
            }

            .award-count {
                font-weight: 700;
                font-size: 18px;
                line-height: 130%;
                color: #000000;
                text-align: center;
            }

            .awards__label {
                text-align: center;
            }

            #assignElectionCheckinsDrawer,
            #electionParticipantsProgressDrawer,
            #electionParticipantDetailsDrawer {
                --bottom-height: 0px;
                --bottom-height-half: 0px;
            }

            .challenges-wrapper {
                width: 100%;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                box-sizing: border-box;
            }

            .challenge-row {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 8px 5px;
                box-sizing: border-box;
                border-bottom: 1px solid #DADADA;
                gap: 10px;
            }

            .challenge-row:hover {
                background-color: var(--theme-border-color);
            }

            .header-row {
                cursor: pointer;
                width: 100%;
                align-items: center;
                font-weight: 700;
                display: flex;
                height: 40px;
                background-color: var(--theme-border-color);
                gap: 10px;
            }

            .header-row mwc-icon {
                font-size: 20px;
            }

            .header-cell,
            .data-cell {
                align-items: center;
            }

            .header-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .header-cell.text {
                text-align: left;
                width: 15%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .header-cell.number {
                text-align: center;
                width: 12%;
                justify-content: center;
            }

            .header-cell.primary {
                flex: 1;
            }

            .data-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .data-cell.text {
                text-align: left;
                width: 15%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .data-cell.number {
                text-align: center;
                width: 12%;
                justify-content: center;
            }

            .data-cell.primary {
                flex: 1;
            }

            .action-button {
                font-size: 16px;
                border-radius: 8px;
                margin: 10px 0;
                --mdc-typography-button-text-transform: capitalize;
            }

            .aqua-blue {
                --mdc-theme-primary: var(--main-secondary-color);
            }

        `;
    }
}
